import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API, useAuth } from '../auth';
import { useLanguage } from '../i18n';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Copy, FileText, Truck, CheckCircle2, CreditCard } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';

export default function OrdersListPage({ scope = 'all' }) {
  const { t, getProductName } = useLanguage();
  const { user, isClient, isAdmin } = useAuth();
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [memo, setMemo] = useState('');
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    if (selectedOrder) setMemo(selectedOrder.payment_memo || '');
  }, [selectedOrder]);

  const confirmPayment = async () => {
    if (!selectedOrder) return;
    setConfirming(true);
    try {
      if (memo !== selectedOrder.payment_memo) {
        await axios.patch(`${API}/orders/${selectedOrder.id}/payment-status`, { payment_memo: memo });
      }
      await axios.post(`${API}/orders/${selectedOrder.id}/confirm-payment`);
      toast.success('결제 확인 완료 · SMS отправлен клиенту');
      await fetchOrders();
      setSelectedOrder(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка подтверждения');
    } finally { setConfirming(false); }
  };

  const setPaymentStatus = async (orderId, status) => {
    try {
      await axios.patch(`${API}/orders/${orderId}/payment-status`, { payment_status: status });
      toast.success('Статус обновлён');
      fetchOrders();
    } catch { toast.error('Ошибка'); }
  };

  const fetchOrders = async () => {
    try {
      let url = `${API}/orders`;
      if (isClient && user?.company_id) {
        url += `?company_id=${user.company_id}`;
      }
      const res = await axios.get(url);
      setOrders(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const formatCurrency = (val) => `₩${Number(val).toLocaleString('ko-KR')}`;

  const statusClasses = {
    new: 'status-new',
    in_production: 'status-in-production',
    packed: 'status-packed',
    shipped: 'status-shipped',
    delivered: 'status-delivered',
    cancelled: 'status-cancelled',
  };

  const paymentBadges = {
    pending: { label: '입금대기', cls: 'bg-amber-100 text-amber-800' },
    paid: { label: '결제확인', cls: 'bg-emerald-100 text-emerald-800' },
    partial: { label: '부분입금', cls: 'bg-orange-100 text-orange-800' },
    overdue: { label: '미납', cls: 'bg-red-100 text-red-800' },
  };

  const copyPaymentInfo = (order) => {
    const ref = `${order.company?.client_code}-${order.order_number}`;
    const text = `결제 정보 (Payment Info)
회사 / Company: ${order.company?.company_name}
주문 번호 / Order: ${order.order_number}
금액 / Amount: ${formatCurrency(order.total_amount)}
참조 코드 / Reference: ${ref}

은행 / Bank: KB국민은행
계좌 / Account: 123-456-7890
예금주 / Holder: Craft Food Factory`;
    navigator.clipboard.writeText(text);
    toast.success(t('copied'));
  };

  const copyDeliveryInfo = (order) => {
    const items = order.order_items?.map(i =>
      `- ${getProductName(i.product)} × ${i.ordered_quantity}`
    ).join('\n');
    const text = `배송 정보 (Delivery Info)
회사 / Company: ${order.company?.company_name}
수령인 / Recipient: ${order.delivery_location?.recipient_name}
전화 / Phone: ${order.delivery_location?.recipient_phone}
주소 / Address: ${order.delivery_location?.address}
메모 / Notes: ${order.delivery_location?.delivery_notes || '-'}

제품 / Products:
${items}`;
    navigator.clipboard.writeText(text);
    toast.success(t('copied'));
  };

  const handleRepeatOrder = async (order) => {
    try {
      const items = order.order_items.map(i => ({
        product_id: i.product_id,
        ordered_quantity: i.ordered_quantity
      }));
      const res = await axios.post(`${API}/orders`, {
        delivery_location_id: order.delivery_location_id,
        items,
        notes: `Repeat of ${order.order_number}`
      });
      toast.success(`Order ${res.data.order_number} created!`);
      fetchOrders();
    } catch (err) {
      toast.error('Failed to repeat order');
    }
  };

  return (
    <div className="space-y-6" data-testid="orders-list-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">{t('orders')}</h1>
        <p className="text-gray-500 mt-1">{orders.length} {t('orders').toLowerCase()}</p>
      </div>

      {orders.length === 0 ? (
        <Card className="p-12 text-center">
          <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No orders yet</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-left text-xs text-gray-500 uppercase tracking-wide">
                  <th className="px-4 py-3 font-medium">{t('orderNumber')}</th>
                  {!isClient && <th className="px-4 py-3 font-medium">{t('company')}</th>}
                  <th className="px-4 py-3 font-medium">{t('plannedShipmentDate')}</th>
                  <th className="px-4 py-3 font-medium text-right">{t('totalAmount')}</th>
                  <th className="px-4 py-3 font-medium">{t('status')}</th>
                  <th className="px-4 py-3 font-medium">결제</th>
                  <th className="px-4 py-3 font-medium text-right">{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(order => (
                  <tr key={order.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`order-list-row-${order.order_number}`}>
                    <td className="px-4 py-3 font-mono text-xs">{order.order_number}</td>
                    {!isClient && <td className="px-4 py-3">{order.company?.company_name}</td>}
                    <td className="px-4 py-3 text-gray-600 cff-text-numeric">
                      {order.planned_shipment_date && new Date(order.planned_shipment_date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right cff-text-numeric font-medium">{formatCurrency(order.total_amount)}</td>
                    <td className="px-4 py-3">
                      <span className={`status-badge ${statusClasses[order.status]}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="outline" size="sm" onClick={() => setSelectedOrder(order)} data-testid={`view-order-${order.order_number}`}>
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Order Detail Dialog */}
      <Dialog open={!!selectedOrder} onOpenChange={(open) => !open && setSelectedOrder(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedOrder && (
            <>
              <DialogHeader>
                <DialogTitle className="font-mono">{selectedOrder.order_number}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500 text-xs uppercase">{t('company')}</p>
                    <p className="font-medium">{selectedOrder.company?.company_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs uppercase">{t('status')}</p>
                    <span className={`status-badge ${statusClasses[selectedOrder.status]}`}>
                      {selectedOrder.status}
                    </span>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs uppercase">{t('plannedShipmentDate')}</p>
                    <p className="font-medium cff-text-numeric">{new Date(selectedOrder.planned_shipment_date).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs uppercase">{t('deliveryLocation')}</p>
                    <p className="font-medium text-xs">{selectedOrder.delivery_location?.address}</p>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Items</h4>
                  <div className="space-y-2">
                    {selectedOrder.order_items?.map(item => (
                      <div key={item.id} className="flex justify-between text-sm py-2 border-b border-gray-100">
                        <span>{getProductName(item.product)}</span>
                        <span className="cff-text-numeric">
                          {item.ordered_quantity} × {formatCurrency(item.unit_price)} = {formatCurrency(item.line_total)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4 space-y-1">
                  <div className="flex justify-between text-sm"><span>{t('subtotal')}</span><span className="cff-text-numeric">{formatCurrency(selectedOrder.subtotal)}</span></div>
                  <div className="flex justify-between text-sm"><span>{t('deliveryCost')}</span><span className="cff-text-numeric">{formatCurrency(selectedOrder.delivery_cost)}</span></div>
                  <div className="flex justify-between text-lg font-bold pt-1 border-t border-gray-100"><span>{t('total')}</span><span className="cff-text-numeric">{formatCurrency(selectedOrder.total_amount)}</span></div>
                </div>
              </div>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                {isAdmin && selectedOrder.payment_status !== 'paid' && (
                  <div className="w-full sm:w-auto sm:mr-auto bg-[#F2EFE6]/70 border border-[#C9A352]/40 rounded-md p-3 mb-2 sm:mb-0">
                    <div className="flex items-center gap-2 mb-2">
                      <CreditCard className="h-4 w-4 text-[#C9A352]" />
                      <p className="text-xs font-bold uppercase tracking-wider">입금 확인</p>
                    </div>
                    <input
                      type="text"
                      placeholder="입금자명 / Имя отправителя"
                      value={memo}
                      onChange={(e) => setMemo(e.target.value)}
                      className="w-full mb-2 px-2 py-1.5 text-sm border border-[#1A1A1A]/20 rounded bg-white font-mono"
                      data-testid="payment-memo-input"
                    />
                    <Button
                      onClick={confirmPayment}
                      disabled={confirming}
                      className="cff-button-primary w-full"
                      data-testid="confirm-payment-btn"
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      {confirming ? '확인 중...' : '결제확인 · Подтвердить оплату'}
                    </Button>
                  </div>
                )}
                <Button variant="outline" onClick={() => copyPaymentInfo(selectedOrder)} data-testid="copy-payment-info">
                  <Copy className="h-4 w-4 mr-2" /> {t('copyPaymentInfo')}
                </Button>
                <Button variant="outline" onClick={() => copyDeliveryInfo(selectedOrder)} data-testid="copy-delivery-info">
                  <Truck className="h-4 w-4 mr-2" /> {t('copyDeliveryInfo')}
                </Button>
                {isClient && (
                  <Button onClick={() => handleRepeatOrder(selectedOrder)} className="cff-button-primary" data-testid="repeat-order-btn">
                    {t('repeatOrder')}
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
